import React from "react";
import {Form, InputNumber, Select} from "antd";

import InputWrap from "../../InputWrap";
import LinearizationDiagram from "./LinearizationDiagram";
import {SettingsSensor, Unit} from "../../../store/branches/sensors/stateTypes";
import {getDefaultRawPoints, cleanFloat} from "../scalingUtils";

import classes from "../EditSensorsTypes.module.scss";

const {Option} = Select;

interface SensorScalingFieldsProps {
    sensor: SettingsSensor
    units?: Unit[]
    form: any
    titlePrefix: string
}

// The Unit Name / Decimals / four-point calibration UI shared by every sensor that
// gets scaled — factored out so both the per-group form (NTC, 0006's CLI-only group)
// and the unified Input 9/10 form (CLI/Pulse modes) render identical fields.
const SensorScalingFields: React.FC<SensorScalingFieldsProps> = ({sensor, units, form, titlePrefix}) => {
    return (
        <React.Fragment>
            <InputWrap title={`${titlePrefix} Unit Name`}
                       className={classes.wideInput}
                       key={`unit_name_${sensor?.unit}_${sensor.id}`}>
                <Form.Item name={`${sensor.id} unit`}
                           initialValue={sensor?.unit}
                >
                    <Select>
                        {
                            units?.map((item: Unit, index: number) => (
                                <Option value={item?.name}
                                        key={`option_unit${item?.id}_${item?.name}${index}`}>{item?.name}</Option>
                            ))
                        }
                    </Select>
                </Form.Item>
            </InputWrap>

            <InputWrap title={`${titlePrefix} Decimals`}
                       className={classes.wideInput}
                       key={`decimals_${sensor?.decimals}_${sensor.id}`}>
                <Form.Item name={`${sensor.id} decimals`}
                           initialValue={sensor?.decimals ?? 3}
                >
                    <InputNumber style={{width: "100%"}} min={0} max={6}/>
                </Form.Item>
            </InputWrap>

            <Form.Item noStyle
                       shouldUpdate={(prevValues, curValues) =>
                           prevValues[`${sensor.id} unit`] !== curValues[`${sensor.id} unit`] ||
                           prevValues[`${sensor.id} raw1`] !== curValues[`${sensor.id} raw1`] ||
                           prevValues[`${sensor.id} raw2`] !== curValues[`${sensor.id} raw2`] ||
                           prevValues[`${sensor.id} scaled1`] !== curValues[`${sensor.id} scaled1`] ||
                           prevValues[`${sensor.id} scaled2`] !== curValues[`${sensor.id} scaled2`]
                       }
            >
                {() => {
                    const unit = form.getFieldValue(`${sensor.id} unit`) ?? sensor?.unit;

                    const hasStoredPoints = sensor?.raw1 != null && sensor?.scaled1 != null
                        && sensor?.raw2 != null && sensor?.scaled2 != null;
                    const hasLegacyCalibration = sensor?.k_value != null && sensor?.c_value != null;
                    const [defaultRaw1, defaultRaw2] = getDefaultRawPoints(unit);

                    let initialRaw1 = defaultRaw1;
                    let initialRaw2 = defaultRaw2;
                    let initialScaled1: number | undefined;
                    let initialScaled2: number | undefined;

                    if (hasStoredPoints) {
                        initialRaw1 = sensor.raw1 as number;
                        initialRaw2 = sensor.raw2 as number;
                        initialScaled1 = sensor.scaled1;
                        initialScaled2 = sensor.scaled2;
                    } else if (hasLegacyCalibration) {
                        initialScaled1 = cleanFloat(sensor.k_value * defaultRaw1 + sensor.c_value);
                        initialScaled2 = cleanFloat(sensor.k_value * defaultRaw2 + sensor.c_value);
                    }

                    const raw1 = form.getFieldValue(`${sensor.id} raw1`) ?? initialRaw1;
                    const raw2 = form.getFieldValue(`${sensor.id} raw2`) ?? initialRaw2;
                    const scaled1 = form.getFieldValue(`${sensor.id} scaled1`) ?? initialScaled1;
                    const scaled2 = form.getFieldValue(`${sensor.id} scaled2`) ?? initialScaled2;

                    return <React.Fragment>
                        <InputWrap title={`${titlePrefix} Point 1 Raw Value (${unit})`}
                                   className={classes.wideInput}
                                   key={`raw1_value${sensor.id}`}>
                            <Form.Item name={`${sensor.id} raw1`}
                                       initialValue={initialRaw1}
                            >
                                <InputNumber style={{width: "100%"}}/>
                            </Form.Item>
                        </InputWrap>

                        <InputWrap title={`${titlePrefix} Point 1 Scaled Output`}
                                   className={classes.wideInput}
                                   key={`scaled1_value${sensor.id}`}>
                            <Form.Item name={`${sensor.id} scaled1`}
                                       initialValue={initialScaled1}
                            >
                                <InputNumber style={{width: "100%"}}/>
                            </Form.Item>
                        </InputWrap>

                        <InputWrap title={`${titlePrefix} Point 2 Raw Value (${unit})`}
                                   className={classes.wideInput}
                                   key={`raw2_value${sensor.id}`}>
                            <Form.Item name={`${sensor.id} raw2`}
                                       initialValue={initialRaw2}
                                       dependencies={[`${sensor.id} raw1`]}
                                       rules={[{
                                           validator: (_, value) =>
                                               value === form.getFieldValue(`${sensor.id} raw1`)
                                                   ? Promise.reject(new Error("Point 2 raw value must differ from Point 1"))
                                                   : Promise.resolve(),
                                       }]}
                            >
                                <InputNumber style={{width: "100%"}}/>
                            </Form.Item>
                        </InputWrap>

                        <InputWrap title={`${titlePrefix} Point 2 Scaled Output`}
                                   className={classes.wideInput}
                                   key={`scaled2_value${sensor.id}`}>
                            <Form.Item name={`${sensor.id} scaled2`}
                                       initialValue={initialScaled2}
                            >
                                <InputNumber style={{width: "100%"}}/>
                            </Form.Item>
                        </InputWrap>

                        <LinearizationDiagram x1={raw1} x2={raw2} y1={scaled1} y2={scaled2} unit={unit}/>
                    </React.Fragment>;
                }}
            </Form.Item>
        </React.Fragment>
    );
};

export default SensorScalingFields;
